import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    ShieldCheck,
    Camera,
    Blinds,
    Star,
    MapPin,
    Award,
    Clock,
    Sparkles,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import QuoteDialog from "@/components/QuoteDialog";
import { BUSINESS, IMAGES } from "@/lib/business";
import { ProcessSteps } from "@/components/sections/Shared";
import SEO, { LOCAL_BUSINESS_JSONLD } from "@/components/SEO";

const SERVICES = [
    {
        icon: ShieldCheck,
        title: "Security Window Films",
        description:
            "Impact-resistant films that reinforce existing glass, deter break-ins and block UV.",
        to: "/security-films/residential",
    },
    {
        icon: Camera,
        title: "Security Cameras",
        description:
            "HD weather-ready cameras with smart detection and mobile alerts for peace of mind.",
        to: "/security-cameras",
    },
    {
        icon: Blinds,
        title: "Blinds & Shutters",
        description:
            "Custom-made blinds, shades and shutters that complete your space, made to measure.",
        to: "/blinds-shutters",
    },
];

const TESTIMONIALS = [
    {
        name: "Priya S.",
        city: "Mississauga, ON",
        quote:
            "GTA Secure installed security film on every ground-floor window and added cameras. Clean install, fair price, and the team was genuinely helpful.",
    },
    {
        name: "Daniel R.",
        city: "Oakville, ON",
        quote:
            "They fitted our entire living room with custom blinds and added a camera at the back gate. Professional from quote to cleanup.",
    },
    {
        name: "Meera & James",
        city: "Brampton, ON",
        quote:
            "We had a break-in attempt last winter — the security film on our patio door held. It paid for itself in one night. Highly recommend.",
    },
];

const FAQS = [
    { q: "Do you service both homes and businesses?", a: "Yes. We install window films, security cameras, blinds and shutters for residential, commercial and multi-unit properties across the Greater Toronto Area." },
    { q: "Will security film change the look of my windows?", a: "Clear safety films are virtually invisible — the glass keeps the same view and light transmission. Privacy, solar and decorative films add the finish you choose." },
    { q: "How long does installation take?", a: "Most residential projects are completed the same day. Larger commercial installs are scheduled to minimise disruption. We confirm a timeline with your quote." },
    { q: "Are quotes really free?", a: "Yes. We provide a no-obligation, transparent quote after an on-site or virtual assessment. No pressure, no upsell." },
    { q: "Where are you located?", a: `We are based at ${BUSINESS.address.full}, and serve the entire Greater Toronto Area.` },
];

export default function HomeV2() {
    const [quoteOpen, setQuoteOpen] = useState(false);
    return (
        <>
            <SEO
                title="Window Films, Security Cameras & Custom Blinds — GTA"
                description="GTA Secure Inc. — Canadian-owned professional installer of security window films, camera systems, and custom blinds & shutters across the Greater Toronto Area."
                path="/v2"
                jsonLd={LOCAL_BUSINESS_JSONLD}
            />

            {/* HERO — logo-forward, full navy stage with gold frame */}
            <section className="relative overflow-hidden bg-navy text-white">
                {/* Decorative concentric rings */}
                <div className="pointer-events-none absolute inset-0 opacity-[0.08]">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full border border-gold" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1400px] h-[1400px] rounded-full border border-gold" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[2000px] h-[2000px] rounded-full border border-gold" />
                </div>
                {/* subtle grain */}
                <div
                    className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
                    style={{
                        backgroundImage:
                            "radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)",
                        backgroundSize: "3px 3px",
                    }}
                />

                <div className="relative container-x pt-20 md:pt-24 pb-20 md:pb-24 text-center">
                    <p className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/40 text-gold text-xs font-semibold uppercase tracking-[0.2em]">
                        <Sparkles className="h-3.5 w-3.5" />
                        Canadian Owned · Est. 2025
                    </p>

                    <h1 className="mt-10 font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] max-w-4xl mx-auto">
                        Making the GTA <span className="text-gold">Safe Again.</span>
                    </h1>
                    <p className="mt-6 text-lg md:text-xl text-white/75 max-w-2xl mx-auto leading-relaxed">
                        Premium security window films, smart camera systems, and custom
                        blinds &amp; shutters — installed with care across the Greater
                        Toronto Area.
                    </p>

                    <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                        <button
                            data-testid="v2-hero-quote-button"
                            onClick={() => setQuoteOpen(true)}
                            className="btn-gold"
                        >
                            Get a Free Quote <ArrowRight className="h-4 w-4" />
                        </button>
                        <a
                            href={BUSINESS.phoneHref}
                            className="btn-outline-navy !border-white/40 !text-white hover:!bg-white hover:!text-navy"
                        >
                            Call {BUSINESS.phone}
                        </a>
                    </div>

                    <div className="mt-16 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
                        <Stat big="1500+" label="Homes Protected" />
                        <Stat big="200+" label="Businesses Secured" />
                        <Stat big="9+ yrs" label="Experience" />
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="section bg-white">
                <div className="container-x">
                    <div className="text-center max-w-3xl mx-auto">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> Our services <span className="gold-rule ml-3" />
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Three expert solutions, one trusted team.
                        </h2>
                        <p className="mt-5 text-slate-600">
                            From invisible safety films to beautiful custom blinds, we secure
                            and style every opening of your home or workplace.
                        </p>
                    </div>

                    <div className="mt-14 grid md:grid-cols-3 gap-6">
                        {SERVICES.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <Link
                                    to={s.to}
                                    key={s.title}
                                    data-testid={`v2-service-card-${i}`}
                                    className="group card-premium p-8 text-center flex flex-col items-center"
                                >
                                    <div className="h-16 w-16 rounded-2xl bg-navy text-gold flex items-center justify-center mb-5 group-hover:scale-105 transition-transform">
                                        <Icon className="h-7 w-7" />
                                    </div>
                                    <h3 className="font-display text-xl font-semibold text-navy">
                                        {s.title}
                                    </h3>
                                    <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                                        {s.description}
                                    </p>
                                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold group-hover:gap-3 transition-all">
                                        Learn more <ArrowRight className="h-4 w-4" />
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Logo strip / brand trust band */}
            <section className="bg-[#FDFCFB] border-y border-gray-100">
                <div className="container-x py-10 flex flex-wrap items-center justify-center gap-8 md:gap-16">
                    <img
                        src={BUSINESS.logo}
                        alt="GTA Secure Inc. — Making GTA Safe Again"
                        className="h-16 md:h-20 w-auto max-w-[380px] object-contain opacity-90"
                    />
                    <div className="hidden md:block h-10 w-px bg-gray-200" />
                    <div className="text-center md:text-left">
                        <p className="font-display text-lg font-semibold text-navy">
                            Making GTA Safe Again.
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                            Canadian owned. Locally installed. Backed by a workmanship guarantee.
                        </p>
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="section bg-white">
                <div className="container-x">
                    <div className="max-w-3xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> How we work
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            A simple, honest process — from quote to installation.
                        </h2>
                    </div>
                    <div className="mt-12">
                        <ProcessSteps
                            steps={[
                                { title: "Get a quote", description: "Tell us about your space. We'll send a transparent, no-obligation quote — fast." },
                                { title: "Professional installation", description: "Our trained installers handle the job cleanly, precisely, and respectfully of your property." },
                                { title: "Feel safe & at home", description: "Enjoy better security, comfort, and style — backed by our workmanship guarantee." },
                            ]}
                        />
                    </div>
                </div>
            </section>

            {/* Why us - badge style */}
            <section className="section bg-[#FDFCFB]">
                <div className="container-x">
                    <div className="text-center max-w-3xl mx-auto">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> Why GTA Secure <span className="gold-rule ml-3" />
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Canadian-owned. Locally trusted.
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {[
                            { icon: MapPin, title: "Local GTA team", desc: "Serving Greater Toronto Area homes & businesses." },
                            { icon: Award, title: "Certified installers", desc: "Trained, licensed, and fully insured professionals." },
                            { icon: ShieldCheck, title: "Premium products", desc: "Top-tier security films, cameras & custom blinds." },
                            { icon: Clock, title: "On-time delivery", desc: "Clear timelines. Clean installs. Respectful of your space." },
                        ].map((w, i) => {
                            const Icon = w.icon;
                            return (
                                <div key={w.title} className="card-premium p-7 text-center">
                                    <div className="h-12 w-12 mx-auto rounded-xl bg-navy text-gold flex items-center justify-center mb-4">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <h3 className="font-semibold text-navy">{w.title}</h3>
                                    <p className="text-sm text-slate-600 mt-2">{w.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Gallery */}
            <section className="section bg-white">
                <div className="container-x">
                    <div className="text-center max-w-3xl mx-auto">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> Portfolio <span className="gold-rule ml-3" />
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Real installs, real transformations.
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
                        {IMAGES.gallery.map((src, idx) => (
                            <div
                                key={src}
                                className={`overflow-hidden rounded-2xl bg-navy/5 ${
                                    idx === 0 || idx === 3 ? "md:row-span-2 aspect-[3/4] md:aspect-[3/5]" : "aspect-[4/3]"
                                }`}
                            >
                                <img src={src} alt={`Gallery ${idx + 1}`} className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="section bg-[#FDFCFB]">
                <div className="container-x">
                    <div className="text-center max-w-2xl mx-auto">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> Reviews <span className="gold-rule ml-3" />
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Homeowners &amp; businesses across the GTA trust us.
                        </h2>
                    </div>
                    <div className="mt-12 grid md:grid-cols-3 gap-6">
                        {TESTIMONIALS.map((t) => (
                            <div key={t.name} className="card-premium p-7">
                                <div className="flex items-center gap-1 text-gold">
                                    {[...Array(5)].map((_, j) => (
                                        <Star key={j} className="h-4 w-4 fill-[#C9A961]" />
                                    ))}
                                </div>
                                <p className="mt-4 text-slate-700 leading-relaxed">"{t.quote}"</p>
                                <div className="mt-6 pt-5 border-t border-gray-100">
                                    <p className="font-semibold text-navy">{t.name}</p>
                                    <p className="text-sm text-slate-500">{t.city}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="section bg-white">
                <div className="container-x grid lg:grid-cols-[1fr_1.3fr] gap-12">
                    <div>
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> FAQ
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Quick answers, honest guidance.
                        </h2>
                        <p className="mt-5 text-slate-600 max-w-md">
                            Still have questions? Call us at{" "}
                            <a href={BUSINESS.phoneHref} className="text-gold font-semibold hover:underline">
                                {BUSINESS.phone}
                            </a>
                            .
                        </p>
                    </div>
                    <Accordion type="single" collapsible className="w-full">
                        {FAQS.map((f, i) => (
                            <AccordionItem value={`v2-faq-${i}`} key={f.q} className="border-b border-gray-200">
                                <AccordionTrigger className="text-left font-semibold text-navy text-base md:text-lg py-5 hover:no-underline">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* Final CTA — logo-forward */}
            <section className="bg-navy relative overflow-hidden">
                <div className="pointer-events-none absolute inset-0 opacity-[0.08]">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full border border-gold" />
                </div>
                <div className="relative container-x py-20 md:py-24 text-center">
                    <img
                        src={BUSINESS.logo}
                        alt="GTA Secure Inc."
                        className="h-24 md:h-32 w-auto max-w-[420px] object-contain mx-auto bg-white rounded-2xl p-4 shadow-[0_20px_60px_-10px_rgba(201,169,97,0.35)]"
                    />
                    <h2 className="mt-8 font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white max-w-3xl mx-auto leading-tight">
                        Ready to secure and beautify your space?
                    </h2>
                    <p className="mt-4 text-white/70 max-w-xl mx-auto">
                        Free on-site assessment. Transparent quote. No pressure, ever.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        <button onClick={() => setQuoteOpen(true)} className="btn-gold">
                            Request Free Quote <ArrowRight className="h-4 w-4" />
                        </button>
                        <a
                            href={BUSINESS.phoneHref}
                            className="btn-outline-navy !border-white/40 !text-white hover:!bg-white hover:!text-navy"
                        >
                            Call {BUSINESS.phone}
                        </a>
                    </div>
                </div>
            </section>

            <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
        </>
    );
}

function Stat({ big, label }) {
    return (
        <div className="text-center">
            <p className="font-display text-2xl md:text-3xl font-bold text-gold">{big}</p>
            <p className="text-xs md:text-sm text-white/60 mt-1">{label}</p>
        </div>
    );
}
